<template>
  <div class="ApplicationDetail">
    <div class="header">
      <router-link
        :to="{
          name: 'externalProjectDetail',
          params: { id: $route.params.projectId },
        }"
        class="ApplicationDetail__back"
      >
        &larr; Back to Project
      </router-link>
      <div class="header-title">Application Detail</div>
    </div>

    <div v-if="loading" class="ApplicationDetail__loading">Loading...</div>

    <div v-else-if="fetchError" class="ApplicationDetail__error">
      <span>{{ fetchError }}</span>
      <button class="button button-primary" @click="load">Retry</button>
    </div>

    <template v-else-if="currentApplication">
      <div class="panel">
        <div class="ApplicationDetail__meta">
          <div class="ApplicationDetail__meta-row">
            <span class="ApplicationDetail__meta-label">Name</span>
            <span>{{ currentApplication.name }}</span>
          </div>
          <div class="ApplicationDetail__meta-row">
            <span class="ApplicationDetail__meta-label">Type</span>
            <span class="ApplicationDetail__type-tag">{{
              currentApplication.type
            }}</span>
          </div>
          <div class="ApplicationDetail__meta-row">
            <span class="ApplicationDetail__meta-label">Status</span>
            <status-badge :status="currentApplication.status" />
          </div>
          <div
            v-if="currentApplication.application_client_id"
            class="ApplicationDetail__meta-row"
          >
            <span class="ApplicationDetail__meta-label">Client ID</span>
            <span class="ApplicationDetail__mono">{{
              currentApplication.application_client_id
            }}</span>
          </div>
          <div
            v-if="currentApplication.redirect_url"
            class="ApplicationDetail__meta-row"
          >
            <span class="ApplicationDetail__meta-label">Redirect URL</span>
            <span class="ApplicationDetail__mono">{{
              currentApplication.redirect_url
            }}</span>
          </div>
          <div
            v-if="currentApplication.website_url"
            class="ApplicationDetail__meta-row"
          >
            <span class="ApplicationDetail__meta-label">Website URL</span>
            <span class="ApplicationDetail__mono">{{
              currentApplication.website_url
            }}</span>
          </div>
          <div
            v-if="currentApplication.bundle_id"
            class="ApplicationDetail__meta-row"
          >
            <span class="ApplicationDetail__meta-label">Bundle ID</span>
            <span class="ApplicationDetail__mono">{{
              currentApplication.bundle_id
            }}</span>
          </div>
          <div
            v-if="currentApplication.package_name"
            class="ApplicationDetail__meta-row"
          >
            <span class="ApplicationDetail__meta-label">Package Name</span>
            <span class="ApplicationDetail__mono">{{
              currentApplication.package_name
            }}</span>
          </div>
          <div class="ApplicationDetail__meta-row">
            <span class="ApplicationDetail__meta-label">Submitted At</span>
            <span>{{
              currentApplication.inserted_at | moment('MMM DD, YYYY HH:mm')
            }}</span>
          </div>
        </div>

        <div class="ApplicationDetail__actions">
          <button
            v-if="currentApplication.status !== 'accepted' && (!currentProject || currentProject.status !== 'rejected')"
            class="button button-success"
            @click="openModal('accept')"
          >
            Accept
          </button>
          <button
            v-if="currentApplication.status !== 'rejected'"
            class="button button-danger"
            @click="openModal('reject')"
          >
            Reject
          </button>
        </div>
      </div>
    </template>

    <action-confirm-modal
      v-if="modal.visible"
      :title="`${modal.action === 'accept' ? 'Accept' : 'Reject'} Application?`"
      :content="`Are you sure you want to ${modal.action} &quot;${currentApplication.name}&quot;? This action cannot be undone.`"
      :loading="modal.loading"
      :error-message="modal.error"
      :require-delete-confirmation="modal.action === 'reject'"
      @confirm="confirmAction"
      @cancel="closeModal"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import StatusBadge from './StatusBadge.vue';
import ActionConfirmModal from './ActionConfirmModal.vue';

const { mapGetters, mapActions, mapMutations } =
  createNamespacedHelpers('externalProjects');

export default {
  name: 'ApplicationDetail',
  components: { StatusBadge, ActionConfirmModal },
  data() {
    return {
      loading: false,
      fetchError: null,
      modal: {
        visible: false,
        action: null,
        loading: false,
        error: null,
      },
    };
  },
  computed: {
    ...mapGetters(['currentApplication', 'currentProject']),
    projectName() {
      return this.currentProject
        ? this.currentProject.name
        : this.$route.params.projectId;
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.clearCurrentApplication();
  },
  methods: {
    ...mapActions([
      'fetchApplication',
      'fetchProject',
      'acceptApplication',
      'rejectApplication',
    ]),
    ...mapMutations(['clearCurrentApplication']),
    async load() {
      this.loading = true;
      this.fetchError = null;
      const [appResult] = await Promise.all([
        this.fetchApplication(this.$route.params.id),
        !this.currentProject
          ? this.fetchProject(this.$route.params.projectId)
          : Promise.resolve({}),
      ]);
      this.loading = false;
      if (appResult.error) {
        if (appResult.error.status === 404) {
          this.$router.push({
            name: 'externalProjectDetail',
            params: { id: this.$route.params.projectId },
          });
          return;
        }
        this.fetchError = this.getErrorMessage(appResult.error);
      }
    },
    openModal(action) {
      this.modal = { visible: true, action, loading: false, error: null };
    },
    closeModal() {
      this.modal = {
        visible: false,
        action: null,
        loading: false,
        error: null,
      };
    },
    async confirmAction() {
      this.modal.loading = true;
      this.modal.error = null;
      const action =
        this.modal.action === 'accept'
          ? this.acceptApplication
          : this.rejectApplication;
      const { error } = await action(this.$route.params.id);
      this.modal.loading = false;
      if (error) {
        this.modal.error = this.getErrorMessage(error);
        return;
      }
      this.closeModal();
    },
    getErrorMessage(error) {
      if (error && error.body && error.body.message) {
        return error.body.message;
      }
      const messages = {
        401: 'Your session has expired. Please log in again.',
        403: "You don't have permission to perform this action.",
        404: 'This item could not be found.',
        422: 'This action is no longer available for this item.',
        500: 'Something went wrong. Please try again.',
      };
      return (
        messages[error && error.status] ||
        'Something went wrong. Please try again.'
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.ApplicationDetail {
  &__back {
    display: inline-block;
    margin-bottom: 8px;
    color: #337ab7;
    font-size: 13px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__breadcrumb {
    font-size: 13px;
    color: #666;
    margin-bottom: 20px;

    a {
      color: #337ab7;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__meta {
    margin-bottom: 24px;
  }

  &__meta-row {
    display: flex;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;
    align-items: center;
  }

  &__meta-label {
    width: 160px;
    font-weight: 600;
    color: #555;
    flex-shrink: 0;
  }

  &__mono {
    font-family: monospace;
    font-size: 13px;
    background: #f4f4f4;
    padding: 2px 6px;
    border-radius: 3px;
  }

  &__type-tag {
    display: inline-block;
    background: #e9ecef;
    color: #495057;
    padding: 2px 10px;
    border-radius: 3px;
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 600;
  }

  &__actions {
    display: flex;
    gap: 8px;

    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__loading {
    padding: 24px;
    color: #666;
    font-size: 14px;
  }

  &__error {
    padding: 14px 16px;
    background: #f8d7da;
    color: #721c24;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }
}
</style>
